import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Loader2, AlertTriangle, Wallet, Undo2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

/**
 * Shared Cancel Trip dialog — opened from the "Cancel Trip" button AND from
 * the Status dropdown's "Cancelled" option on EditQuote.js, and from the
 * Accounts "Needs cancellation decision" filtered list on QuotesList.js.
 * Agents can always cancel; only Admin/Accounts ever see or submit the
 * fee/refund/credit money step.
 */
const CancelTripDialog = ({ tripId, open, onOpenChange, onDone }) => {
  const { user } = useAuth();
  const canDecide = ['admin', 'accounts'].includes((user?.role || '').toLowerCase());
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [feeRetained, setFeeRetained] = useState('0');
  const [refundAmount, setRefundAmount] = useState('0');
  const [creditAmount, setCreditAmount] = useState('0');
  const [reason, setReason] = useState('');
  const [refundMethod, setRefundMethod] = useState('bank_transfer');

  const load = useCallback(async () => {
    if (!tripId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/trips/${tripId}/cancellation-info`, { withCredentials: true });
      setInfo(r.data);
      const rest = Math.max(0, Number(r.data.amountReceived || 0));
      setFeeRetained('0');
      setRefundAmount(rest.toString());
      setCreditAmount('0');
      setReason('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load cancellation info');
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  }, [tripId, onOpenChange]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const amountReceived = Number(info?.amountReceived || 0);
  const fee = Math.max(0, Number(feeRetained) || 0);
  const refund = Math.max(0, Number(refundAmount) || 0);
  const credit = Math.max(0, Number(creditAmount) || 0);
  const totalAllocated = fee + refund + credit;
  const overLimit = totalAllocated > amountReceived + 0.01;
  const needsMoneyStep = amountReceived > 0;
  const alreadyCancelled = info?.status === 'cancelled';
  const alreadyDecided = info?.cancellationMoneyStatus === 'decided';
  const showDecisionForm = needsMoneyStep && canDecide && !alreadyDecided;

  const handleSimpleCancel = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/trips/${tripId}/cancel`, {}, { withCredentials: true });
      toast.success('Trip cancelled');
      onOpenChange(false);
      onDone && onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to cancel trip');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecisionSubmit = async () => {
    if (!reason.trim()) { toast.error('Reason is required'); return; }
    if (overLimit) { toast.error('Fee + refund + credit cannot exceed the amount received'); return; }
    setSubmitting(true);
    try {
      if (!alreadyCancelled) {
        await axios.post(`${BACKEND_URL}/api/trips/${tripId}/cancel`, {}, { withCredentials: true });
      }
      await axios.post(`${BACKEND_URL}/api/trips/${tripId}/cancel-decision`, {
        feeRetained: fee,
        refundAmount: refund,
        creditAmount: credit,
        reason: reason.trim(),
        refundMethod,
      }, { withCredentials: true });
      toast.success('Cancellation decision recorded');
      onOpenChange(false);
      onDone && onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to record cancellation decision');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="cancel-trip-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-rose-500" /> Cancel Trip
          </DialogTitle>
          <DialogDescription>
            {info ? `${info.displayTripId || info.tripId} · ${info.clientName || 'Traveller'}` : 'Loading…'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : alreadyDecided ? (
          <div className="space-y-3 text-sm" data-testid="cancellation-decided-summary">
            <p className="text-slate-600">A cancellation decision was already recorded for this trip:</p>
            <div className="rounded-lg border border-slate-200 p-3 space-y-1 bg-slate-50">
              <div>Amount received: <b>{inr(info.cancellationDecision?.amountReceived)}</b></div>
              <div>Fee retained: <b>{inr(info.cancellationDecision?.feeRetained)}</b></div>
              {info.cancellationDecision?.refundAmount > 0 && (
                <div>Refunded: <b>{inr(info.cancellationDecision?.refundAmount)}</b></div>
              )}
              {info.cancellationDecision?.creditAmount > 0 && (
                <div>Kept as credit: <b>{inr(info.cancellationDecision?.creditAmount)}</b></div>
              )}
              <div className="text-slate-500 text-xs pt-1">Reason: {info.cancellationDecision?.reason}</div>
              <div className="text-slate-400 text-[11px]">
                Decided by {info.cancellationDecision?.decidedBy} on {(info.cancellationDecision?.decidedAt || '').slice(0, 10)}
              </div>
            </div>
          </div>
        ) : !needsMoneyStep ? (
          <div className="space-y-4 text-sm" data-testid="cancel-no-money-step">
            <p className="text-slate-600">No money was received for this booking. This trip will be marked Cancelled — no financial decision is needed.</p>
          </div>
        ) : !canDecide ? (
          <div className="space-y-4 text-sm" data-testid="cancel-agent-pending-step">
            <p className="text-slate-600">
              <b>{inr(amountReceived)}</b> was received for this booking. This trip will be marked <b>Cancelled</b> and flagged for Accounts to record
              the fee / refund / credit decision.
            </p>
          </div>
        ) : (
          <div className="space-y-4 text-sm" data-testid="cancel-decision-form">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex items-center justify-between">
              <span className="text-slate-600">Amount received</span>
              <span className="font-semibold font-mono" data-testid="cancel-amount-received">{inr(amountReceived)}</span>
            </div>
            <div>
              <Label>Cancellation fee retained (₹)</Label>
              <Input
                type="number" min="0" value={feeRetained}
                onChange={(e) => setFeeRetained(e.target.value)}
                data-testid="cancel-fee-input" className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Refund to original mode (₹)</Label>
                <Input
                  type="number" min="0" value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  data-testid="cancel-refund-input" className="mt-1"
                />
              </div>
              <div>
                <Label>Keep as credit (₹)</Label>
                <Input
                  type="number" min="0" value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                  data-testid="cancel-credit-input" className="mt-1"
                />
              </div>
            </div>
            {refund > 0 && (
              <div>
                <Label>Refund method</Label>
                <Select value={refundMethod} onValueChange={setRefundMethod}>
                  <SelectTrigger className="mt-1" data-testid="cancel-refund-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                    <SelectItem value="original_payment_reversal">Original payment reversal</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Reason</Label>
              <Textarea
                value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this trip being cancelled?"
                data-testid="cancel-reason-input" className="mt-1"
              />
            </div>
            {overLimit && (
              <p className="text-xs text-rose-600 flex items-center gap-1" data-testid="cancel-overlimit-error">
                <AlertTriangle className="h-3.5 w-3.5" /> Fee + refund + credit (₹{totalAllocated.toLocaleString('en-IN')}) exceeds the amount received (₹{amountReceived.toLocaleString('en-IN')}).
              </p>
            )}
            {fee > 0 && (
              <p className="text-[11px] text-slate-400 flex items-center gap-1">
                <Undo2 className="h-3 w-3" /> The ₹{fee.toLocaleString('en-IN')} fee is recorded separately — tax treatment pending CA, no GST/TCS, no invoice.
              </p>
            )}
            {credit > 0 && (
              <p className="text-[11px] text-slate-400 flex items-center gap-1">
                <Wallet className="h-3 w-3" /> ₹{credit.toLocaleString('en-IN')} will be added to {info.clientName}'s wallet credit balance.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-trip-dialog-close-btn">Close</Button>
          {!loading && !alreadyDecided && (
            showDecisionForm ? (
              <Button
                onClick={handleDecisionSubmit}
                disabled={submitting || overLimit}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="cancel-trip-submit-decision-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm Cancellation'}
              </Button>
            ) : (
              <Button
                onClick={handleSimpleCancel}
                disabled={submitting}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="cancel-trip-confirm-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Cancel Trip'}
              </Button>
            )
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CancelTripDialog;
